<div
  class="personal-data-editor"
  [class.personal-data-editor--admin]="layout() === 'admin'"
  [class.personal-data-editor--patrol]="layout() === 'patrol'"
>
  <section class="personal-data-editor__section">
    <header class="personal-data-editor__header">
      <div class="personal-data-editor__header-copy">
        <h2 class="personal-data-editor__title">
          {{ 'app.components.personalDataEditor.contactTitle' | translate }}
        </h2>
        <p class="personal-data-editor__subtitle">
          {{ 'app.components.personalDataEditor.contactSubtitle' | translate }}
        </p>
      </div>
    </header>

    <div class="personal-data-editor__body">
      <form class="personal-data-editor__form" [formGroup]="form">
        <app-input-field
          formControlName="firstName"
          [label]="'app.components.personalDataEditor.firstName' | translate"
          [placeholder]="'app.components.personalDataEditor.firstNamePlaceholder' | translate"
          [validationErrors]="firstNameValidation()"
        />
        <app-input-field
          formControlName="lastName"
          [label]="'app.components.personalDataEditor.lastName' | translate"
          [placeholder]="'app.components.personalDataEditor.lastNamePlaceholder' | translate"
          [validationErrors]="lastNameValidation()"
        />
        <app-input-field
          formControlName="email"
          [type]="'email'"
          [label]="'app.components.personalDataEditor.email' | translate"
          [placeholder]="'app.components.personalDataEditor.emailPlaceholder' | translate"
          [validationErrors]="emailValidation()"
        />
        <app-input-field
          formControlName="phone"
          [label]="'app.components.personalDataEditor.phone' | translate"
          [placeholder]="'app.components.personalDataEditor.phonePlaceholder' | translate"
        />
        <app-input-field
          formControlName="country"
          [label]="'app.components.personalDataEditor.country' | translate"
          [placeholder]="'app.components.personalDataEditor.countryPlaceholder' | translate"
        />
        <app-input-field
          class="personal-data-editor__field--full"
          formControlName="address"
          [label]="'app.components.personalDataEditor.address' | translate"
          [placeholder]="'app.components.personalDataEditor.addressPlaceholder' | translate"
        />
      </form>

      <div class="personal-data-editor__actions">
        <app-parent-button
          variant="primary"
          [loading]="isSaving()"
          [disabled]="!canSave()"
          (clicked)="save()"
        >
          {{ 'app.components.personalDataEditor.save' | translate }}
        </app-parent-button>
      </div>
    </div>
  </section>

  <section class="personal-data-editor__section">
    <header class="personal-data-editor__header">
      <div class="personal-data-editor__header-copy">
        <h2 class="personal-data-editor__title">
          {{ 'app.components.personalDataEditor.identityTitle' | translate }}
        </h2>
        <p class="personal-data-editor__subtitle">
          {{ 'app.components.personalDataEditor.identitySubtitle' | translate }}
        </p>
      </div>
    </header>

    <div class="personal-data-editor__body">
      <div class="personal-data-editor__avatar-row">
        <div class="personal-data-editor__avatar">
          @if (user()?.personalData?.avatar) {
            <img [src]="user()?.personalData?.avatar" alt="" />
          } @else {
            <span>{{ user()?.personalData | initials }}</span>
          }
        </div>

        <div class="personal-data-editor__avatar-actions">
          <input
            type="file"
            #fileInput
            hidden
            accept="image/png, image/jpeg, image/jpg"
            (change)="fileChangeEvent($event)"
          />
          <app-parent-button
            variant="ghost"
            [disabled]="isUploading()"
            [loading]="isUploading()"
            (clicked)="fileInput.click()"
          >
            @if (!isUploading()) {
              {{ 'app.components.personalDataEditor.changePhoto' | translate }}
            } @else {
              {{ 'app.components.personalInfo.uploading' | translate }}
            }
          </app-parent-button>
          <app-parent-button
            variant="icon"
            [attr.aria-label]="'app.components.personalDataEditor.removePhoto' | translate"
            [disabled]="isUploading()"
            (clicked)="deleteAvatar()"
          >
            <img src="/public/svgs/trash-red.svg" alt="" width="16" height="16" aria-hidden="true" />
          </app-parent-button>
        </div>
      </div>
    </div>
  </section>

  @if (showCropper()) {
    <div class="personal-data-editor__cropper" role="dialog" aria-modal="true">
      <div class="personal-data-editor__cropper-panel">
        <header class="personal-data-editor__cropper-header">
          <div class="personal-data-editor__header-copy">
            <h3 class="personal-data-editor__title">
              {{ 'app.components.imageCropper.cropImage' | translate }}
            </h3>
          </div>

          <app-parent-button
            variant="icon"
            [attr.aria-label]="'app.components.imageCropper.cancel' | translate"
            (clicked)="closeCropper()"
          >
            <img src="/public/svgs/x-close.svg" alt="" width="16" height="16" aria-hidden="true" />
          </app-parent-button>
        </header>

        <div class="personal-data-editor__cropper-body">
          @if (isImageLoading() && !hasImageError()) {
            <div class="personal-data-editor__cropper-state">
              <span class="personal-data-editor__spinner" aria-hidden="true"></span>
              <p>{{ 'app.components.imageCropper.loadingImage' | translate }}</p>
            </div>
          }

          @if (hasImageError()) {
            <div class="personal-data-editor__cropper-state personal-data-editor__cropper-state--error">
              <span class="personal-data-editor__error-icon" aria-hidden="true"></span>
              @if (isFileSizeError()) {
                <h4>{{ 'app.components.imageCropper.fileSizeErrorTitle' | translate }}</h4>
                <p>{{ 'app.components.imageCropper.fileSizeErrorMessage' | translate }}</p>
              } @else {
                <h4>{{ 'app.components.imageCropper.loadErrorTitle' | translate }}</h4>
                <p>{{ 'app.components.imageCropper.loadErrorMessage' | translate }}</p>
              }

              <div class="personal-data-editor__cropper-actions parent-dialog-actions">
                <app-parent-button variant="ghost" (clicked)="closeCropper()">
                  {{ 'app.components.imageCropper.cancel' | translate }}
                </app-parent-button>
                @if (!isFileSizeError()) {
                  <app-parent-button variant="primary" (clicked)="retryImageLoad()">
                    {{ 'app.components.imageCropper.retry' | translate }}
                  </app-parent-button>
                }
              </div>
            </div>
          }

          <image-cropper
            [imageChangedEvent]="imageChangedEvent"
            [maintainAspectRatio]="true"
            [aspectRatio]="1"
            format="png"
            (imageCropped)="imageCropped($event)"
            (imageLoaded)="imageLoaded($event)"
            (cropperReady)="cropperReady()"
            (loadImageFailed)="loadImageFailed()"
          />

          @if (isCropperReady()) {
            <div class="personal-data-editor__cropper-actions parent-dialog-actions">
              <app-parent-button variant="ghost" (clicked)="closeCropper()">
                {{ 'app.components.imageCropper.cancel' | translate }}
              </app-parent-button>
              <app-parent-button
                variant="primary"
                [loading]="isUploading()"
                [disabled]="!croppedImage || isUploading()"
                (clicked)="saveCroppedImage()"
              >
                {{ 'app.components.imageCropper.crop' | translate }}
              </app-parent-button>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>
